import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { MdDelete, MdAdd, MdEdit, MdCheck, MdSchedule, MdWarning } from 'react-icons/md';
import { gql, useSuspenseQuery, useMutation } from '@apollo/client';

import { withScreen } from './Screen';
import { TasksList_GetTasksQuery, TasksList_GetTasksQueryVariables } from '../../codegen/graphql';
import { useModalRouter } from '../ModalRouter_context';

const GET_TASKS = gql`
  query TasksList_GetTasks($completed: Boolean) {
    tasks(completed: $completed) {
      id
      title
      description
      orderId
      nextDueDate
      completed
      createdAt
      updatedAt
      
      order {
        id
        orderId
        orderStatus
        customerName
      }
    }
  }
`;

const COMPLETE_TASK = gql`
  mutation TasksList_CompleteTask($id: ID!) {
    completeTask(id: $id) {
      id
      completed
    }
  }
`;

const DELETE_TASK = gql`
  mutation TasksList_DeleteTask($id: ID!) {
    deleteTask(id: $id)
  }
`;

interface TasksListProps {}

function TasksList({}: TasksListProps) {
  const navigate = useNavigate();
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const modalRouter = useModalRouter();
  
  const { data, refetch } = useSuspenseQuery<TasksList_GetTasksQuery, TasksList_GetTasksQueryVariables>(
    GET_TASKS,
    {
      variables: { completed: showCompletedTasks ? undefined : false },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [completeTask] = useMutation(COMPLETE_TASK, {
    onCompleted: () => {
      refetch();
    },
  });

  const [deleteTask] = useMutation(DELETE_TASK, {
    onCompleted: () => {
      refetch();
    },
  });

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedTasks(newSelection);
  };

  const handleRowClick = (taskId: string) => {
    modalRouter.push({ s: 'task', a: { taskId } });
  };

  const handleCompleteTask = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    completeTask({ variables: { id } });
  };

  const handleDeleteTask = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteTask({ variables: { id } });
    }
  };

  const handleDeleteSelected = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedTasks.size} tasks?`)) {
      selectedTasks.forEach(id => {
        deleteTask({ variables: { id } });
      });
      setSelectedTasks(new Set());
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No due date';
    
    const dueDate = new Date(dateString);
    return dueDate.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const isTaskOverdue = (dueDate: string | null | undefined) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900 mr-4">Tasks ({data.tasks.length})</h1>
            <label className="inline-flex items-center">
              <input 
                type="checkbox" 
                checked={showCompletedTasks} 
                onChange={() => setShowCompletedTasks(!showCompletedTasks)}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Show completed tasks</span>
            </label>
          </div>
          
          <div className="flex gap-4">
            {selectedTasks.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <MdDelete size={20} />
                Delete Selected ({selectedTasks.size})
              </button>
            )}

            <button
              onClick={() => navigate('/tasks/new')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MdAdd size={20} />
              New Task
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-12 bg-gray-50 divide-x divide-gray-200">
            <div className="col-span-1 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <input 
                type="checkbox" 
                className="form-checkbox h-5 w-5 text-blue-600"
                onChange={() => {
                  if (selectedTasks.size === data.tasks.length) {
                    setSelectedTasks(new Set());
                  } else {
                    setSelectedTasks(new Set(data.tasks.map(task => task.id)));
                  }
                }}
                checked={selectedTasks.size === data.tasks.length && data.tasks.length > 0}
              />
            </div>
            <div className="col-span-4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </div>
            <div className="col-span-3 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Related Order
            </div>
            <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Due Date
            </div>
            <div className="col-span-2 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </div>
          </div>
          
          {/* Data Rows */}
          <div className="bg-white divide-y divide-gray-200">
            {data.tasks.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No tasks found. Create a new task to get started.
              </div>
            ) : (
              data.tasks.map((task) => (
                <div
                  key={task.id}
                  className={`grid grid-cols-12 hover:bg-gray-50 transition-colors cursor-pointer ${
                    isTaskOverdue(task.nextDueDate) && !task.completed ? 'bg-red-50' : ''
                  }`}
                  onClick={() => handleRowClick(task.id)}
                >
                  <div className="col-span-1 px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      className="form-checkbox h-5 w-5 text-blue-600"
                      checked={selectedTasks.has(task.id)}
                      onChange={() => toggleSelection(task.id)}
                    />
                  </div>
                  <div className="col-span-4 px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {task.completed ? (
                        <MdCheck className="mr-2 text-green-500" size={20} />
                      ) : isTaskOverdue(task.nextDueDate) ? (
                        <MdWarning className="mr-2 text-red-500" size={20} />
                      ) : (
                        <MdSchedule className="mr-2 text-blue-500" size={20} />
                      )}
                      <div className="text-sm font-medium text-gray-900">
                        {task.title}
                      </div>
                    </div>
                    {task.description && (
                      <div className="text-sm text-gray-500 truncate max-w-md">
                        {task.description}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 px-6 py-4 whitespace-nowrap">
                    {task.order ? (
                      <div>
                        <div className="text-sm font-medium text-blue-600">
                          <a 
                            href={`/orders/${task.order!.id}`} 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/orders/${task.order!.id}`);
                            }}
                            className="hover:underline"
                          >
                            #{task.order.orderId}
                          </a>
                        </div>
                        <div className="text-sm text-gray-500">
                          {task.order.customerName}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No related order</span>
                    )}
                  </div>
                  <div className="col-span-2 px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${isTaskOverdue(task.nextDueDate) && !task.completed ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                      {formatDate(task.nextDueDate)}
                      {isTaskOverdue(task.nextDueDate) && !task.completed && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Overdue
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {!task.completed && (
                      <button
                        onClick={(e) => handleCompleteTask(task.id, e)}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        <MdCheck size={20} />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteTask(task.id, e)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Outlet />
    </>
  );
}

export default withScreen(TasksList); 